/*
 * Copyright 2026 Sony Corporation
 */

import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { unzip } from 'fflate';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Scene } from '@babylonjs/core/scene';

// Registers the glTF plugin with the loader.
import '@babylonjs/loaders/glTF/2.0';

import { authorization, readSession, type SketchfabSession } from '../services/sketchfab-auth';
import { cachedBlob } from '../services/asset-cache';

/**
 * Turning a Sketchfab reference into geometry.
 *
 * Unlike Poly Haven, which serves a `.gltf` and its siblings as separate URLs,
 * Sketchfab hands over one **ZIP** on a signed link that lives for minutes
 * (devplan §5.8). So the archive is the allowlist: the `.gltf` inside it can
 * only refer to entries that came in the same file, and anything else has no
 * blob to be rewritten to and therefore cannot be fetched.
 *
 * Every byte moves browser-to-Sketchfab. The token is the user's own and never
 * touches our API, and no Lambda opens an asset file (§5.7).
 */

const BASE = 'https://api.sketchfab.com/v3';

/** The same ceiling the Poly Haven importer uses: a scene has to load in a tab that is also editing. */
const MAX_BYTES = 40 * 1024 * 1024;

/** Never fetched; a name for the cache to file an archive under. */
const ARCHIVE_CACHE = 'https://sketchfab.cache.local/archives/';

const inflate = (archive: Uint8Array): Promise<Record<string, Uint8Array>> =>
  new Promise((resolve, reject) => {
    unzip(archive, (error, files) => (error ? reject(error) : resolve(files)));
  });

/**
 * A zip path as the `.gltf` would spell it.
 *
 * A glTF URI is relative to the file that names it and may be escaped, but the
 * archive's keys are neither, so the two have to be brought to the same form
 * before they can be compared.
 */
const normalise = (uri: string): string => decodeURIComponent(uri).replace(/^\.\//, '');

const fetchDownloadUrl = async (uid: string, session: SketchfabSession, signal: AbortSignal): Promise<string> => {
  const response = await fetch(`${BASE}/models/${encodeURIComponent(uid)}/download`, {
    headers: { Authorization: authorization(session) },
    signal,
  });

  if (response.status === 401 || response.status === 403) {
    throw new Error('Sketchfab refused the download. Reconnect your account and try again.');
  }
  if (!response.ok) {
    throw new Error(`Sketchfab answered ${response.status}.`);
  }

  const body = (await response.json()) as { gltf?: { url?: string; size?: number } };
  if (!body.gltf?.url) {
    throw new Error('Sketchfab offers no glTF for this model.');
  }
  if (body.gltf.size !== undefined && body.gltf.size > MAX_BYTES) {
    throw new Error('The model is larger than this editor will load.');
  }
  return body.gltf.url;
};

/** The band a real object's longest side plausibly falls in: a mug to a lorry. */
const SANE_MIN_METRES = 0.05;
const SANE_MAX_METRES = 30;

/** Beyond this the reading is a guess, not a unit conversion, so nothing is done. */
const MAX_DECADES = 4;

/**
 * What to multiply an import by to bring it into metres.
 *
 * glTF says metres; Sketchfab's authors frequently do not, and a model arrives
 * in centimetres, millimetres or nothing in particular. The correction is the
 * **smallest power of ten** that lands the model back in a plausible band,
 * because a unit conversion is what is actually wrong. Fitting to one target
 * size instead would put a mug and a building on the same number, and aiming
 * at the middle of the band overshoots — a 4.5 m car authored in centimetres
 * wants a factor of 1/100, not whatever gets it nearest 1 m.
 *
 * It cannot always be right: 450 could be a car in centimetres or a genuine
 * 450 m structure, and nothing in the geometry says which. It is biased
 * towards the props and vehicles this editor actually places.
 */
export const unitFactor = (longest: number): number => {
  if (!Number.isFinite(longest) || longest <= 0) {
    return 1;
  }
  for (let decades = 0; decades <= MAX_DECADES; decades++) {
    for (const factor of decades === 0 ? [1] : [10 ** -decades, 10 ** decades]) {
      const corrected = longest * factor;
      if (corrected >= SANE_MIN_METRES && corrected <= SANE_MAX_METRES) {
        return factor;
      }
    }
  }
  return 1;
};

const normaliseUnits = (container: AssetContainer): void => {
  let min: Vector3 | undefined;
  let max: Vector3 | undefined;

  for (const mesh of container.meshes) {
    if (mesh.getTotalVertices() === 0) {
      continue;
    }
    mesh.computeWorldMatrix(true);
    const box = mesh.getBoundingInfo().boundingBox;
    min = min ? Vector3.Minimize(min, box.minimumWorld) : box.minimumWorld.clone();
    max = max ? Vector3.Maximize(max, box.maximumWorld) : box.maximumWorld.clone();
  }

  const factor = unitFactor(min && max ? Math.max(...max.subtract(min).asArray()) : 0);
  if (factor === 1) {
    return;
  }

  for (const node of [...container.meshes, ...container.transformNodes]) {
    // Multiplied, not assigned: the glTF loader puts the right-to-left-handed
    // flip in the root's scale, and overwriting it would mirror the model.
    if (!node.parent) {
      node.scaling.scaleInPlace(factor);
    }
  }
};

export const loadSketchfabModel = async (scene: Scene, uid: string, signal: AbortSignal): Promise<AssetContainer> => {
  // The uid names the bytes; the URL they come on is signed and expires, so
  // keying on it would cache nothing. A hit here skips the download call as
  // well as the download.
  const blob = await cachedBlob(`${ARCHIVE_CACHE}${uid}.zip`, async () => {
    // Read per import rather than injected: the user may connect after the
    // document was opened, and an asset added before that must then just work.
    const session = readSession();
    if (!session) {
      throw new Error('Connect your Sketchfab account to import this model.');
    }

    const url = await fetchDownloadUrl(uid, session, signal);

    // No Authorization here: the link is already signed, and sending a credential
    // to a storage host that did not ask for one is how credentials leak.
    const archive = await fetch(url, { signal });
    if (!archive.ok) {
      throw new Error(`The model could not be downloaded (${archive.status}).`);
    }
    return archive.blob();
  });

  const bytes = new Uint8Array(await blob.arrayBuffer());
  if (bytes.byteLength > MAX_BYTES) {
    throw new Error('The model is larger than this editor will load.');
  }

  const files = await inflate(bytes);
  const entry = Object.keys(files).find((name) => name.toLowerCase().endsWith('.gltf'));
  if (!entry) {
    throw new Error('The Sketchfab archive holds no glTF.');
  }

  const document = JSON.parse(new TextDecoder().decode(files[entry])) as {
    buffers?: { uri?: string }[];
    images?: { uri?: string }[];
  };

  const urls = new Map<string, string>();
  const resolve = (uri: string | undefined): string | undefined => {
    if (!uri || uri.startsWith('data:')) {
      return uri;
    }
    const cached = urls.get(uri);
    if (cached) {
      return cached;
    }

    const content = files[normalise(uri)];
    if (!content) {
      throw new Error(`The model asks for ${uri}, which is not in the archive.`);
    }
    // Sliced because fflate hands back views onto one buffer, and Blob would
    // otherwise keep the whole archive alive behind every texture.
    const blob = URL.createObjectURL(new Blob([content.slice()]));
    urls.set(uri, blob);
    return blob;
  };

  try {
    for (const buffer of document.buffers ?? []) {
      buffer.uri = resolve(buffer.uri);
    }
    for (const image of document.images ?? []) {
      image.uri = resolve(image.uri);
    }

    // A File rather than a URL: the extension is how the loader picks its plugin.
    const file = new File([JSON.stringify(document)], 'asset.gltf', { type: 'model/gltf+json' });
    const container = await LoadAssetContainerAsync(file, scene);
    normaliseUnits(container);
    return container;
  } finally {
    // Babylon has read everything it needs by now; the blobs would otherwise
    // hold the whole asset in memory for the life of the tab.
    for (const blob of urls.values()) {
      URL.revokeObjectURL(blob);
    }
  }
};
